import React, { useMemo, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import { useTranslation, withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { reduxForm, Field, FieldArray } from 'redux-form';
import { compose } from 'redux';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import get from 'lodash/get';
import {
  PageTitle,
  FormUi,
  Grid,
  G,
  PageContent,
  Header,
  Dimmer,
  Loader,
  Segment,
} from 'nallian-shared-ui/lib/components';
import {
  InputFieldValueComponent,
  SelectFieldValueComponent,
  TextAreaFieldValueComponent,
} from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import lookupService from 'nallian-shared-ui/lib/hoc/lookupService';
import { handleKeyDown } from 'nallian-shared-ui/lib/lib/advancedForm';
import { showToaster } from 'nallian-shared-ui/lib/lib/showToaster';
import FormActions from '../components/FormActions';

import { countrycodes } from '../helpers/countrycodes';
import getCommunitiesService from '../services/getCommunities';
import apiRoutes from '../../../app/api/routes';
import organisationsService from '../services/organisationsService';
import { debounceAsyncValidate } from '../../../app/helpers/debouncedAsyncValidate';
import IdentificationCodeFields from '../../../portal/community/members/components/fields/IdentificationCodeFields';
import typesService from '../../identitficationCodeTypes/services/typesService';
import { validateIdentificationCodes } from '../../../portal/community/members/helpers/validateIdentificationCodes';

const FORM_NAME = 'FormOrganisationNew';

const countrycodeslist = map(countrycodes, country => ({
  key: `${country.code}`,
  value: `${country.code}`,
  text: `${country.name}`,
}));

const OrganisationForm = ({
  handleSubmit,
  communitiesFetch,
  onSubmitForm,
  processingForm,
  pageTitle,
  loadingContent,
  asyncValidate,
  getOrganisationsForSelectOptions,
  dispatchOrganisationsGet,
  timezonesOverviewFetch,
  editMode,
  identificationCodeTypes,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();

  useEffect(() => dispatchOrganisationsGet(), [dispatchOrganisationsGet]);

  const asyncValidating = Boolean(
    useSelector(state => get(state, `form.${FORM_NAME}.asyncValidating`))
  );

  const communitiesList = useMemo(
    () =>
      (communitiesFetch.value || []).map(community => ({
        key: `${community.name}`,
        value: `${community.id}`,
        text: `${community.name}`,
      })),
    [communitiesFetch.value]
  );

  const organisations = getOrganisationsForSelectOptions();

  const organisationOptions = useMemo(() => organisations.filter(org => org.value !== id), [
    organisations,
    id,
  ]);

  const timezoneOptions = useMemo(
    () => (timezonesOverviewFetch.value || []).map(v => ({ key: v, value: v, text: v })),
    [timezonesOverviewFetch.value]
  );

  return (
    <>
      <PageTitle>{pageTitle}</PageTitle>
      <PageContent>
        <FormUi
          onSubmit={handleSubmit(onSubmitForm)}
          onKeyDown={event => handleKeyDown(event, handleSubmit(onSubmitForm), asyncValidate)}
        >
          <Segment style={{ background: 'white', marginBottom: '5rem' }} basic>
            <Dimmer active={loadingContent} inverted>
              <Loader />
            </Dimmer>
            {!loadingContent && (
              <>
                <Header style={{ marginTop: '0' }} dividing>
                  {t('general')}
                </Header>
                <Field
                  label={t('organisationName')}
                  type="text"
                  name="organisationName"
                  valueComponent={InputFieldValueComponent}
                  autoFocus
                  disabled={loadingContent}
                />
                <Field
                  label={t('description')}
                  name="description"
                  valueComponent={TextAreaFieldValueComponent}
                  autoHeight
                  autoFocus
                  disabled={loadingContent}
                />
                <Header dividing>{t('location')}</Header>
                <Grid stackable>
                  <G.Row columns={2}>
                    <G.Col>
                      <Field
                        label={t('city')}
                        type="text"
                        name="city"
                        valueComponent={InputFieldValueComponent}
                        disabled={loadingContent}
                      />
                    </G.Col>
                    <G.Col>
                      <Field
                        label={t('country')}
                        name="countryCode"
                        type="select"
                        valueComponent={SelectFieldValueComponent}
                        options={countrycodeslist}
                        disabled={loadingContent}
                        search
                      />
                    </G.Col>
                  </G.Row>
                </Grid>
                <Header dividing>{t('community')}</Header>
                <Grid columns="2">
                  <G.Row>
                    <G.Col>
                      <Field
                        label={t('defaultCommunity')}
                        name="defaultCommunityId"
                        valueComponent={SelectFieldValueComponent}
                        loading={communitiesFetch.pending || loadingContent}
                        disabled={communitiesFetch.pending || loadingContent}
                        options={communitiesList}
                        search
                      />
                    </G.Col>
                  </G.Row>
                </Grid>
                <Header dividing>{t('parentOrganisation')}</Header>
                <Grid columns="2">
                  <G.Row>
                    <G.Col>
                      <Field
                        label={t('parentOrganisation')}
                        name="parentOrganisationId"
                        valueComponent={SelectFieldValueComponent}
                        options={organisationOptions}
                        search
                        clearable
                      />
                    </G.Col>
                  </G.Row>
                </Grid>
                <Header dividing>{t('timeSettings')}</Header>
                <Grid columns="2">
                  <G.Row>
                    <G.Col>
                      <Field
                        search
                        clearable
                        label={t('timezone')}
                        name="timeZone"
                        valueComponent={SelectFieldValueComponent}
                        options={timezoneOptions}
                      />
                    </G.Col>
                  </G.Row>
                </Grid>
                {!editMode && (
                  <>
                    <Header dividing>{t('identificationCodes')}</Header>
                    <Grid columns="1">
                      <G.Row>
                        <G.Col>
                          <FieldArray
                            hideTitle
                            name="identificationCodes"
                            component={IdentificationCodeFields}
                            submitProcessing={processingForm}
                            identificationCodeTypes={identificationCodeTypes}
                          />
                        </G.Col>
                      </G.Row>
                    </Grid>
                  </>
                )}
              </>
            )}
            <FormActions
              onClickSave={handleSubmit(onSubmitForm)}
              processingForm={processingForm}
              asyncValidating={asyncValidating}
            />
          </Segment>
        </FormUi>
      </PageContent>
    </>
  );
};

const asyncValidate = (values, _, props) => {
  if (props.initialValues && values.organisationName === props.initialValues.organisationName) {
    return new Promise(resolve => resolve());
  }

  const url = apiRoutes.getOrganisationsByName(values.organisationName);

  return new Promise((resolve, reject) => {
    axios.get(url).then(response => {
      if (response?.data?.length > 0) {
        const existingOrgs = response.data.filter(
          org => org.organisationName === values.organisationName
        );

        const existingOrg = isArray(existingOrgs) && existingOrgs[0];

        if (existingOrg !== undefined) {
          const errors = {
            organisationName: 'That organisation name is taken',
          };
          showToaster('error', errors.organisationName);
          reject(errors);
        }
        resolve();
      } else {
        resolve();
      }
    });
  });
};

// Debounce your asyncValidate function
const debouncedAsyncValidate = debounceAsyncValidate(asyncValidate, 300);

function validate(values, { t, identificationCodeTypes }) {
  const errors = {};

  if (!values.organisationName || (values.organisationName && !values.organisationName.trim())) {
    errors.organisationName = 'Organisation name is required';
  }

  if (!values.city || (values.city && !values.city.trim())) {
    errors.city = 'City is required';
  }

  if (values.countryCode) {
    errors.countryCode = null;
  }

  validateIdentificationCodes(values?.identificationCodes, errors, identificationCodeTypes, t);

  return errors;
}

OrganisationForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  asyncValidate: PropTypes.func.isRequired,
  communitiesFetch: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  onSubmitForm: PropTypes.func.isRequired,
  processingForm: PropTypes.bool.isRequired,
  pageTitle: PropTypes.string.isRequired,
  loadingContent: PropTypes.bool,
  initialValues: PropTypes.object,
  getOrganisationsForSelectOptions: PropTypes.func.isRequired,
  dispatchOrganisationsGet: PropTypes.func.isRequired,
  timezonesOverviewFetch: PropTypes.object.isRequired,
  identificationCodeTypes: PropTypes.array,
  editMode: PropTypes.bool,
};

OrganisationForm.defaultProps = {
  loadingContent: false,
  initialValues: undefined,
  editMode: false,
  identificationCodeTypes: [],
};

const mapStateToProps = (_, ownProps) => ({
  validate: values =>
    validate(values, { identificationCodeTypes: ownProps.identificationCodeTypes, t: ownProps.t }),
});

export default compose(
  getCommunitiesService,
  typesService,
  organisationsService,
  withTranslation(),
  connect(mapStateToProps),
  lookupService({
    resource: 'timezonesOverview',
    url: apiRoutes.getTimezones(),
  }),
  reduxForm({
    validate,
    asyncValidate: debouncedAsyncValidate,
    asyncBlurFields: ['organisationName'],
    form: FORM_NAME,
    enableReinitialize: true,
  })
)(OrganisationForm);
